// TODO VUE3: UNIT TEST ME
import { computed, ref } from 'vue'
import type { Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { LocationQueryRaw, LocationQueryValue, RouteLocationRaw, RouteQueryAndHash } from 'vue-router'
import { useSduiI18n } from 'src/composables/useSduiI18n.ts'
import { useCurrentUser } from 'src/composables/useCurrentUser.ts'
import { ENTITY_TYPE } from 'src/config/constants.ts'
import type { SchoolYear } from 'src/types/store/models/school.ts'
import { useCompare } from '@/composables/utils/useCompare.ts'
import { useDates } from '@/composables/utils/useDates.ts'
import { useClone } from '@/composables/utils/useClone.ts'
import { useMoment } from '@/composables/utils/useMoment.ts'
import { useAxios } from '@/composables/utils/useAxios.ts'

export const filterTypes = {
  all: 'ALL',
  custom: 'CUSTOM',
  is_open: 'is_open',
  excused: 'EXCUSED',
  unexcused: 'UNEXCUSED',
  pending: 'PENDING',
}

export const dataKeys = {
  absences: 'absences',
  entries: 'entries',
  homeworks: 'homeworks',
  notices: 'notices',
  statistics: 'statistics',
}

export type FilterTypeKey = keyof typeof filterTypes;

type FetchParams = {
  url: string;
  with_deleted?: boolean;
  begins_at?: string;
  ends_at?: string;
  filters?: FilterTypeKey;
  notice_categories?: string | string[];
  absence_types?: string[];
  grades_enrollment_id?: number[];
  limit?: number;
  entity?: Entities
};

type FilterType = {
  id: string;
  name: string;
  group?: string;
  hidden?: boolean;
};

type FilterDateRange = {
  begins_at: string;
  ends_at: string;
};

type Entities = 'grades' | 'courses' | 'users' | 'allGrades' | 'allCourses' | 'allUsers';

type Filters = {
  begins_at?: string;
  ends_at?: string;
  entity?: Entities;
  filters?: FilterTypeKey;
  with_deleted?: boolean;
  absence_types?: string[];
  notice_categories?: string | string[];
  grades_enrollment_id?: number[];
};

type NormalizedQuery = {
  absence_types?: number[];
  with_deleted?: boolean;
  [key: string]: any;
};

export function useClassbookFilter() {
  const { compare } = useCompare()
  const { dateFormat } = useDates()
  const { clone } = useClone()
  const { moment } = useMoment()
  const { axios } = useAxios()

  const route = useRoute()
  const router = useRouter()
  const { me } = useCurrentUser()
  const { t } = useSduiI18n()

  // +++++++++++ DATA VAR +++++++++++
  const isFilterInitialized: Ref<boolean> = ref(false)
  const isFilterModalVisible: Ref<boolean> = ref(false)
  const hasFilterChanged: Ref<boolean> = ref(false)
  const currentFilters: Ref<Filters> = ref({})

  // +++++++++++ METHODS +++++++++++
  /**
   * Initializes the filter by setting the default query params based on the current school year.
   * This is called when the classbook page is loaded or when entity changes.
   */
  const initFilters = async (): Promise<void> => {
    const queryParams = getDefaultQueryForFilter(true)
    if (!queryParams) return

    await router.replace(queryParams)

    const immutableQueryParams = clone(queryParams.query as object)
    currentFilters.value = {
      ...currentFilters.value,
      ...immutableQueryParams,
    }
    isFilterInitialized.value = true
  }

  /**
   * Applies new filter query params to the current route.
   */
  const applyFilters = async (newFilterQueryParams?: LocationQueryRaw): Promise<void> => {
    if (!isFilterInitialized.value) return

    if (isFilterModalVisible.value) toggleFilterModal(false)

    const newRouteQueryParams = getQueryForFilter({
      ...currentFilters.value,
      filters: filterTypes.custom as FilterTypeKey,
      ...newFilterQueryParams,
      entity: route.query.entity as Entities,
    })
    if (!newRouteQueryParams || typeof newRouteQueryParams === 'string') return

    currentFilters.value = { ...newRouteQueryParams.query }
    await router.replace(newRouteQueryParams)
    hasFilterChanged.value = !hasFilterChanged.value
  }

  /**
   * Resets filter to default values and ONLY applies filter type such as "ALL", "is_open", etc.
   */
  const applyFilterType = async (newFilterType: string): Promise<void> => {
    if (!isFilterInitialized.value) return

    const defaultQueryParams = getDefaultQueryForFilter(false, newFilterType)
    if (!defaultQueryParams) return

    await applyFilters({ ...defaultQueryParams.query })
  }

  const handleFilterCategory = (filterCategory: string | string[] | undefined): string[] | undefined => {
    if (!filterCategory) return

    if (Array.isArray(filterCategory) && filterCategory.length) return filterCategory

    if (typeof filterCategory === 'string') return [filterCategory]
  }

  /**
   * Fetches filtered items based on the provided parameters.
   */
  // TODO VUE3: Type API response as return type of method
  const fetchFilteredItems = async (
    dataKey: string,
    page: number,
    search: string,
    fetchParams: FetchParams,
  ): Promise<any> => {
    const {
      url,
      with_deleted,
      begins_at,
      ends_at,
      filters,
      notice_categories,
      absence_types,
      grades_enrollment_id,
      limit = 10,
    } = fetchParams

    // Do not mutate function parameters directly
    let localNoticeCategories = notice_categories
    localNoticeCategories = handleFilterCategory(localNoticeCategories)

    // Do not mutate function parameters directly
    let localAbsenceTypes = absence_types
    localAbsenceTypes = handleFilterCategory(localAbsenceTypes)

    const params = () => ({
      begins_at,
      ends_at,
      limit,
      ...(with_deleted && { with_deleted: with_deleted }),
      ...addGroupedFilter(filters, dataKey),
      page,
      ...(search && { search: search }),
      ...(localNoticeCategories && Array.isArray(localNoticeCategories) && { notice_categories: localNoticeCategories.map((item) => item).join('|') }),
      ...(grades_enrollment_id && { grades_enrollment_id }),
      ...(localAbsenceTypes && Array.isArray(localAbsenceTypes) && { absence_type: localAbsenceTypes.map((item) => item).join('|') }),
    })

    return axios.get(url, { params: params(), timeout: import.meta.env.VITE_CLASSBOOK_EXTENDED_REQUEST_TIMEOUT })
  }

  /**
   * Adds a grouped filter based on the provided filters and dataKey.
   */
  const addGroupedFilter = (filters: FilterTypeKey | undefined, dataKey: string): Record<string, string | number> => {
    const [filterData] = getAvailableFilterTypesForKey(dataKey).filter((filter) => filter.id === filters)
    if (!filters) return {}
    if (filters === filterTypes.custom && filters === filterTypes.all) return {}
    if (!filterData.group) return { [filters]: filterTypes[filters] ? '1' : filters }

    return { [filterData.group]: filterTypes[filters] ? '1' : filters }
  }

  /**
   * Normalizes the route query parameters to ensure they are in the correct format.
   * This is mostly important for boolean values such as `with_deleted`, which will be transformed to
   * a string when read from route.query
   */
  const normalizeRouteQuery = (): NormalizedQuery => {
    type customQueryParams = {
      absence_types?: string | string[] | number | number[];
      with_deleted?: boolean | string;
    };
    const normalizedQuery = clone(route.query) as customQueryParams

    // Normalize with_deleted to boolean
    if (normalizedQuery.with_deleted != null) {
      normalizedQuery.with_deleted = /^true$/i.test(normalizedQuery.with_deleted as string)
    }

    // Absence types
    if (normalizedQuery.absence_types) {
      // loop through normalizedQuery.absence_types and ensure the value is a Number
      normalizedQuery.absence_types = Array.isArray(normalizedQuery.absence_types)
        ? normalizedQuery.absence_types.map((item) => Number(item))
        : [Number(normalizedQuery.absence_types)]
    }

    return normalizedQuery as NormalizedQuery
  }

  /**
   * Returns the query parameters for a given filter.
   */
  const getQueryForFilter = (newFilter: Filters): undefined | RouteLocationRaw => {
    // Do not update query params if they are the same. This would cause unnecessary watcher workload
    if (compare(normalizeRouteQuery(), newFilter)) return

    // only set certain filters if filter type is "CUSTOM"
    const isCustomFilter = newFilter.filters === filterTypes.custom
    const {
      /* eslint-disable @typescript-eslint/no-unused-vars */
      begins_at,
      ends_at,
      notice_categories,
      absence_types,
      grades_enrollment_id,
      with_deleted,
      /* eslint-disable @typescript-eslint/no-unused-vars */
      ...rest
    } = route.query

    const typeSpecificQueryParams: LocationQueryRaw | object = isCustomFilter
      ? {
        ...(newFilter.begins_at && { begins_at: newFilter.begins_at }),
        ...(newFilter.ends_at && { ends_at: newFilter.ends_at }),
        ...(newFilter.notice_categories && { notice_categories: newFilter.notice_categories }),
        ...(newFilter.absence_types && { absence_types: newFilter.absence_types }),
        ...(newFilter.grades_enrollment_id && { grades_enrollment_id: newFilter.grades_enrollment_id }),
        ...(newFilter.with_deleted != null && { with_deleted: newFilter.with_deleted }),
      }
      : {}

    return {
      params: { ...route.params },
      query: {
        ...(isCustomFilter ? { ...route.query } : { ...rest }),
        ...typeSpecificQueryParams,
        ...(newFilter.filters && { filters: newFilter.filters }),
      },
    }
  }

  /**
   * Returns the default query params for a filter type, such as "ALL", "is_open", etc.
   */
  const getDefaultQueryForFilter = (
    isInit: boolean = false,
    newFilterType: string | LocationQueryValue[] = currentFilterType.value,
  ): undefined | RouteQueryAndHash => {
    // default date range is the current school year
    const defaultFilterDateRange = getDefaultFilterDateRange()

    currentFilters.value = {
      ...currentFilters.value,
      begins_at: defaultFilterDateRange.begins_at,
      ends_at: defaultFilterDateRange.ends_at,
    }

    // In case of page reload, we need to re-apply existing filters from the query
    // We also need to transform certain query params back to array, such as absence_types and notice_categories
    const existingQuery = isInit
      ? {
        ...route.query,
        ...(route.query.notice_categories && {
          notice_categories: Array.isArray(route.query.notice_categories)
            ? [...route.query.notice_categories]
            : [route.query.notice_categories],
        }),
        ...(route.query.absence_types && {
          absence_types: Array.isArray(route.query.absence_types) ? [...route.query.absence_types] : [route.query.absence_types],
        }),
      }
      : {}

    const newDefaultQueryParams = {
      query: {
        ...existingQuery,
        filters: newFilterType,
        entity: route.query.entity,
      },
    }

    if (!isInit && compare(normalizeRouteQuery(), newDefaultQueryParams.query)) return

    return newDefaultQueryParams
  }

  /**
   * Returns the available filter types for a given key from dataKeys.
   */
  const getAvailableFilterTypesForKey = (key: string): FilterType[] => {
    // CHRONIC
    if (key === dataKeys.entries) {
      return [
        {
          id: filterTypes.all,
          name: t('classbook.filters.all'),
        },
        {
          id: filterTypes.is_open,
          name: t('classbook.overview.not_complete'),
        },
        {
          id: filterTypes.custom,
          name: t('classbook.filters.filter_modal_btn'),
        },
      ]
    }

    // NOTICES
    if (key === dataKeys.notices) {
      return [
        {
          id: filterTypes.all,
          name: t('classbook.filters.all'),
        },
        {
          id: filterTypes.custom,
          name: t('classbook.filters.filter_modal_btn'),
        },
      ]
    }

    // STATISTICS, HOMEWORK, NOTICES
    if ([dataKeys.statistics, dataKeys.homeworks].includes(key)) {
      return [
        {
          id: filterTypes.all,
          name: t('classbook.filters.all'),
          hidden: key !== dataKeys.notices,
        },
        {
          id: filterTypes.custom,
          name: t('classbook.filters.filter'),
        },
      ]
    }

    // ABSENCES
    return [
      {
        id: filterTypes.all,
        name: t('classbook.filters.all'),
      },
      {
        id: filterTypes.excused,
        name: t('classbook.filters.excused'),
        group: 'absence_type_state',
      },
      {
        id: filterTypes.unexcused,
        name: t('classbook.filters.unexcused'),
        group: 'absence_type_state',
      },
      {
        id: filterTypes.pending,
        name: t('classbook.filters.pre_excused'),
        group: 'absence_type_state',
      },
      {
        id: filterTypes.custom,
        name: t('classbook.filters.filter_modal_btn'),
      },
    ]
  }

  const toggleFilterModal = (isVisible: boolean): void => {
    isFilterModalVisible.value = isVisible

    if (isVisible) {
      // Ensure that filter modal always has a default date range set or the currently selected date range
      currentFilters.value = {
        ...getDefaultFilterDateRange(),
        ...currentFilters.value,
      }
    }
  }

  /**
   * Returns the default date range for the filter, which is the whole school year.
   * Except when no school year is selected, then it defaults to today and tomorrow.
   */
  const getDefaultFilterDateRange = (): FilterDateRange => {
    if (!schoolYearIdByClassbookId.value) {
      const begins_at = dateFormat.timestamp(moment())
      const ends_at = dateFormat.timestamp(moment().add(1, 'days'))
      return { begins_at, ends_at }
    }
    const [classbookSchoolYear] = me.value.school.timetable.schoolyears.filter(
      (item: SchoolYear) => item.id === schoolYearIdByClassbookId.value,
    )
    return {
      begins_at: dateFormat.timestamp(moment(classbookSchoolYear.begins_at).startOf('day')),
      ends_at: dateFormat.timestamp(moment(classbookSchoolYear.ends_at).endOf('day')),
    }
  }

  /**
   * Handles the closing of the custom filter modal without submitting (close btn / backdrop click / ESC key).
   * Close the filter modal and resets to the previous filter type and filters.
   */
  const onCustomFilterTypeModalClose = async (): Promise<void> => {
    toggleFilterModal(false)

    // if "CUSTOM" filter type is already selected we do not need to reset the filters
    if (currentFilterType.value === filterTypes.custom) {
      // But we do want to forget any user input that wasn't submitted, so we just grab the previous filters from the query
      currentFilters.value = clone(filtersFromQuery.value)
      return
    }

    // Reset back to previous filter type (so the correct radio button is selected)
    await applyFilterType(String(currentFilterType.value))
  }

  // +++++++++++ COMPUTED +++++++++++
  const currentFilterType = computed(() => {
    return route.query.filters as string || filterTypes.all
  })

  const filtersFromQuery = computed<Filters>(() => {
    const filteredQueryParams = Object.entries(route.query).filter(
      ([key]) => !['sdui-category-filter', 'absence-cat-selection'].includes(key),
    )
    const { begins_at, ends_at, state, absence_types, with_deleted, ...all } = Object.fromEntries(filteredQueryParams)

    return {
      ...(begins_at && { begins_at: String(begins_at) }),
      ...(ends_at && { ends_at: String(ends_at) }),
      ...all,
      ...(absence_types && { absence_types: [...absence_types] }),
      ...(with_deleted != null && { with_deleted: /^true$/i.test(String(with_deleted)) ? 1 : 0 }),
    } as Filters
  })

  const schoolYearIdByClassbookId = computed(() => {
    // get schoolYearId by the currently selected classbook
    const schoolYearByClassbookId = me.value.school?.timetable?.schoolyears.find(
      (schoolYear: SchoolYear) => Number(schoolYear.classbook.id) === Number(route.params.classbook),
    )
    return schoolYearByClassbookId?.id
  })

  const isNotAllEntity = computed(() => {
    return (
      currentEntityId.value !== ENTITY_TYPE.allGrades &&
      currentEntityId.value !== ENTITY_TYPE.allCourses &&
      currentEntityId.value !== ENTITY_TYPE.allUsers
    )
  })

  const currentEntityName = computed(() => {
    return route.params.entity as 'grades' | 'courses' | 'users'
  })

  const currentEntityId = computed(() => {
    return route.params.id as string
  })

  const currentClassbookId = computed(() => {
    return route.params.classbook as string
  })

  return {
    isFilterInitialized,
    isFilterModalVisible,
    hasFilterChanged,
    currentFilters,
    currentFilterType,
    initFilters,
    applyFilters,
    applyFilterType,
    fetchFilteredItems,
    getAvailableFilterTypesForKey,
    toggleFilterModal,
    getDefaultFilterDateRange,
    onCustomFilterTypeModalClose,
    addGroupedFilter,
    getQueryForFilter,
    getDefaultQueryForFilter,
    normalizeRouteQuery,
    filtersFromQuery,
    isNotAllEntity,
    currentEntityName,
    currentEntityId,
    currentClassbookId,
  }
}
